import React, { useRef, useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, Typography, Box, IconButton,
  CircularProgress, Divider,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";
import EmailComposeDialog from "./EmailComposeDialog"; 
import { useGetEmailTemplateQuery } from "../../../Api/attoDeskApi";
import { IEmailTemplate } from "../../../Api/Interface/api.interface";

// ─── Props ────────────────────────────────────────────────────────────────────

interface DeclarationDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderNo: string;
  invoiceNo: string;
  customerName: string;
  customerEmail: string;
  principalAmount: number;
  interestRate: number;
  totalMonths: number;
  issueDate: string;
  workOrderId: string | number;
  staffName: string;
  shopName?: string;
  onSuccess?: () => void;
  viewOnly?: boolean;
  savedDeclaration?: any;
  companyLogoUrl?: string;
  companyName?: string;
  companySlog?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(n: number) {
  return n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}

function todayFormatted() {
  return new Date().toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });
}

function canvasIsBlank(canvas: HTMLCanvasElement | null): boolean {
  if (!canvas) return true;
  const ctx = canvas.getContext("2d");
  if (!ctx) return true;
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return false;
  }
  return true;
}

function isRealSignature(dataUri: string): boolean {
  return (
    typeof dataUri === "string" &&
    dataUri.length > 200 &&
    dataUri.startsWith("data:image")
  );
}

const fetchImageAsBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url, { cache: "no-cache" });
    if (!response.ok) return "";
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror  = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch {
    return "";
  }
};

// ─── CompanyHeader ────────────────────────────────────────────────────────────

interface CompanyHeaderProps {
  logoUrl?: string; companyName?: string; companySlog?: string;
  companyAddress?: string; companyPhone?: string; companyEmail?: string;
}

const CompanyHeader: React.FC<CompanyHeaderProps> = ({
  logoUrl, companyName, companySlog, companyAddress, companyPhone, companyEmail,
}) => (
  <Grid container justifyContent="space-between" alignItems="center"
    sx={{ borderTop: "20px solid #000000", pt: 1.5, pb: 1.5, mb: 2 }}>
    <Grid item xs={4} sm={3} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
      {logoUrl ? (
        <Box component="img" src={logoUrl} alt="Company Logo"
          sx={{ maxHeight: 64, maxWidth: 110, objectFit: "contain" }} />
      ) : companyName ? (
        <Box sx={{ width: 52, height: 52, borderRadius: 1.5, backgroundColor: "primary.main",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="h6" fontWeight={700} color="white">
            {companyName.charAt(0).toUpperCase()}
          </Typography>
        </Box>
      ) : null}
    </Grid>
    <Grid item xs={8} sm={9} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
      <Grid container direction="column" sx={{ textAlign: "right", pr: 0.5 }}>
        {companyName    && <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0, lineHeight: 1.3 }}>{companyName}</Typography>}
        {companySlog    && <Typography variant="caption"  sx={{ mb: 0, color: "text.secondary" }}>{companySlog}</Typography>}
        {companyAddress && <Typography variant="caption"  sx={{ mb: 0, color: "text.secondary", whiteSpace: "nowrap" }}>{companyAddress}</Typography>}
        {(companyPhone || companyEmail) && (
          <Typography variant="caption" sx={{ mb: 0, color: "text.secondary" }}>
            {[companyPhone, companyEmail].filter(Boolean).join("  |  ")}
          </Typography>
        )}
      </Grid>
    </Grid>
  </Grid>
);

// ─── SignaturePanel ───────────────────────────────────────────────────────────

interface SignaturePanelProps {
  label: string; canvasRef: React.RefObject<HTMLCanvasElement>;
  signed: boolean; onSigned: () => void; onCleared: () => void;
  pinValue: string; onPinChange: (v: string) => void;
  onPinConfirm: () => void; pinError: string;
}

const SignaturePanel: React.FC<SignaturePanelProps> = ({
  label, canvasRef, signed, onSigned, onCleared,
}) => {
  const drawing = useRef(false);

  const getPos = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement
  ) => {
    const rect = canvas.getBoundingClientRect();
    const src  = "touches" in e ? e.touches[0] : (e as React.MouseEvent<HTMLCanvasElement>);
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d")!;
    const pos = getPos(e, canvasRef.current);
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!drawing.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d")!;
    ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 2; ctx.lineCap = "round";
    const pos = getPos(e, canvasRef.current);
    ctx.lineTo(pos.x, pos.y); ctx.stroke();
  };

  const endDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!drawing.current || !canvasRef.current) return;
    drawing.current = false;
    if (!canvasIsBlank(canvasRef.current)) onSigned();
  };

  const handleClear = () => {
    if (!canvasRef.current) return;
    canvasRef.current.getContext("2d")!.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onCleared();
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: signed ? "success.main" : "divider",
      borderRadius: 2, p: 2, backgroundColor: "background.paper", transition: "border-color 0.2s" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
          {label}
        </Typography>
        {signed && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>Signed</Typography>
          </Box>
        )}
      </Box>
      <canvas ref={canvasRef} width={260} height={120}
        style={{ width: "100%", height: 120, display: "block", background: "#f8f8fb",
          border: "1px dashed #ccc", borderRadius: 6, cursor: "crosshair", touchAction: "none" }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
      <Box display="flex" justifyContent="flex-end" mt={0.5}>
        <Button size="small" onClick={handleClear} sx={{ fontSize: 11, color: "text.secondary" }}>Clear</Button>
      </Box>
    </Box>
  );
};

// ─── DeclarationDialog ────────────────────────────────────────────────────────

const DeclarationDialog: React.FC<DeclarationDialogProps> = ({
  open, onClose, workOrderNo, invoiceNo, customerName, customerEmail,
  principalAmount, interestRate, totalMonths, issueDate, workOrderId,
  staffName, shopName = "Authorized Representative", onSuccess,
  viewOnly = false, savedDeclaration = null,
  companyLogoUrl, companyName, companySlog, companyAddress, companyPhone, companyEmail,
}) => {
  const API_URL = process.env.REACT_APP_API_URL;

  

  const custCanvasRef  = useRef<HTMLCanvasElement>(null);
  const staffCanvasRef = useRef<HTMLCanvasElement>(null);

  const [custSigned,     setCustSigned]     = useState(false);
  const [staffSigned,    setStaffSigned]    = useState(false);
  const [custSignedVia,  setCustSignedVia]  = useState<"canvas" | "pin" | null>(null);
  const [staffSignedVia, setStaffSignedVia] = useState<"canvas" | "pin" | null>(null);
  const [custPin,        setCustPin]        = useState("");
  const [staffPin,       setStaffPin]       = useState("");
  const [custPinError,   setCustPinError]   = useState("");
  const [staffPinError,  setStaffPinError]  = useState("");
  const [savedCustSig,   setSavedCustSig]   = useState<string>("");
  const [savedStaffSig,  setSavedStaffSig]  = useState<string>("");

  const DEMO_CUSTOMER_PIN = "1234";
  const DEMO_STAFF_PIN    = "0000";

  const [step,            setStep]            = useState<"form" | "success">("form");
  const [loading,         setLoading]         = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);  // ← NEW

  // ── Computed email defaults ─────────────────────────────────────────────
  const defaultEmailTo      = customerEmail ?? "";
  const defaultEmailSubject = `Loan Closure Declaration – ${invoiceNo}`;
  const defaultEmailMessage =
    `Dear ${customerName},\n\n` +
    `Please find attached your loan closure declaration for Invoice ${invoiceNo}. ` +
    `All amounts have been settled in full and your pledged item(s) are ready for collection.\n\n` +
    `Thank you for your business.`;

    const { data: tplData } = useGetEmailTemplateQuery({ searchText: "" });
const templates: IEmailTemplate[] = Array.isArray(tplData?.data)
  ? (tplData?.data as IEmailTemplate[]) : [];

const declarationTemplate = templates.find((t) =>
  t.template_name?.toLowerCase().includes("declaration")
) ?? templates[0];

const resolvedSubject = declarationTemplate?.subject
  ?.replace(/\{\{customerName\}\}/g, customerName)
  ?.replace(/\{\{invoiceNo\}\}/g, invoiceNo)
  ?? defaultEmailSubject;

const resolvedMessage = declarationTemplate?.message
  ?.replace(/\{\{customerName\}\}/g, customerName)
  ?.replace(/\{\{invoiceNo\}\}/g, invoiceNo)
  ?? "";

  const declarationTemplateName = declarationTemplate?.template_name ?? "Declaration";

  // ── Reset on open ───────────────────────────────────────────────────────
  useEffect(() => {
    if (open && !viewOnly) {
      setCustSigned(false); setStaffSigned(false);
      setCustSignedVia(null); setStaffSignedVia(null);
      setCustPin(""); setStaffPin("");
      setCustPinError(""); setStaffPinError("");
      setSavedCustSig(""); setSavedStaffSig("");
      setStep("form");
      setTimeout(() => {
        [custCanvasRef, staffCanvasRef].forEach((ref) => {
          if (ref.current)
            ref.current.getContext("2d")?.clearRect(0, 0, ref.current.width, ref.current.height);
        });
      }, 50);
    }
  }, [open, viewOnly]);

  useEffect(() => {
    if (!open || !viewOnly || !savedDeclaration) return;
    const custRaw  = String(savedDeclaration.customer_signature ?? "");
    const staffRaw = String(savedDeclaration.staff_signature    ?? "");
    setSavedCustSig(custRaw);
    setSavedStaffSig(staffRaw);
    setCustSignedVia(custRaw.startsWith("data:image")  ? "canvas" : (savedDeclaration.customer_signed_via ?? "pin"));
    setStaffSignedVia(staffRaw.startsWith("data:image") ? "canvas" : (savedDeclaration.staff_signed_via   ?? "pin"));
    setStep("success");
  }, [open, viewOnly, savedDeclaration]);

  const bothSigned = custSigned && staffSigned;

  const handleCustPinConfirm = () => {
    if (custPin.length < 4) { setCustPinError("PIN must be at least 4 digits."); return; }
    if (custPin === DEMO_CUSTOMER_PIN) { setCustPinError(""); setCustSigned(true); setCustSignedVia("pin"); }
    else setCustPinError("Incorrect PIN. Please try again.");
  };

  const handleStaffPinConfirm = () => {
    if (staffPin.length < 4) { setStaffPinError("PIN must be at least 4 digits."); return; }
    if (staffPin === DEMO_STAFF_PIN) { setStaffPinError(""); setStaffSigned(true); setStaffSignedVia("pin"); }
    else setStaffPinError("Incorrect PIN. Please try again.");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const custSig  = custCanvasRef.current?.toDataURL("image/png") ?? "";
      const staffSig = staffCanvasRef.current?.toDataURL("image/png") ?? "";
      const custVia  = !canvasIsBlank(custCanvasRef.current)  ? "canvas" : "pin";
      const staffVia = !canvasIsBlank(staffCanvasRef.current) ? "canvas" : "pin";
      setSavedCustSig(custSig); setSavedStaffSig(staffSig);
      setCustSignedVia(custVia); setStaffSignedVia(staffVia);
      const payload = {
        work_order_id: workOrderId, work_order_no: workOrderNo, invoice_no: invoiceNo,
        customer_name: customerName, principal_amount: principalAmount,
        interest_rate: interestRate, total_months: totalMonths, issue_date: issueDate,
        declaration_date: new Date().toISOString(), staff_name: staffName,
        customer_signature: custSig, staff_signature: staffSig,
        customer_signed_via: custVia, staff_signed_via: staffVia,
      };
      const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" };
      await axios.post(`${API_URL}/save-declaration`, payload, { headers });
      setStep("success");
      onSuccess?.();
    } catch (err) {
      console.error("Declaration save error:", err);
      alert("Failed to save declaration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Print HTML builder ──────────────────────────────────────────────────
  const getPrintHTML = (contentHTML: string) => `
    <html>
      <head>
        <title>Declaration_${workOrderNo}</title>
        <style>
          @page { size: A4 portrait; margin: 0; }
          @media print {
            html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          body { font-family: helvetica, sans-serif; padding: 16mm; color: #000; margin: 0; box-sizing: border-box; }
          .header { display: table; width: 100%; border-top: 20px solid #000; padding-top: 12px; margin-bottom: 16px; }
          .logo { display: table-cell; vertical-align: middle; width: 130px; padding-right: 12px; }
          .logo img { max-height: 60px; max-width: 120px; object-fit: contain; }
          .company-right { display: table-cell; vertical-align: middle; text-align: right; word-break: break-word; }
          .company-right h2 { margin: 0; font-size: 14px; }
          .company-right p { margin: 2px 0; font-size: 11px; color: #555; }
          .divider { border: none; border-top: 1px solid #ddd; margin: 12px 0; }
          .title { text-align: center; font-size: 20px; font-weight: bold; margin-bottom: 4px; }
          .subtitle { text-align: center; font-size: 10px; color: #888; margin-bottom: 12px; }
          .body-text { font-size: 12px; line-height: 1.8; }
          .body-text p { margin: 4px 0; }
          .sig-row { display: table; width: 100%; margin-top: 24px; }
          .sig-box { display: table-cell; width: 48%; padding-right: 4%; vertical-align: top; }
          .sig-label { font-size: 10px; color: #888; margin-bottom: 6px; }
          .sig-box img { width: 100%; height: 80px; object-fit: contain; border: 1px dashed #ccc; background: #f8f8fb; display: block; }
          .sig-pin { text-align: center; padding: 16px; border: 1px dashed #ccc; color: green; font-size: 11px; }
          .sig-line { border-top: 1px solid #333; margin-top: 8px; padding-top: 4px; font-size: 10px; }
        </style>
      </head>
      <body>${contentHTML}</body>
    </html>
  `;

  const buildPrintableHTML = () => {
    const logoHTML = companyLogoUrl
      ? `<img src="${companyLogoUrl}" alt="logo" style="max-height:60px;max-width:120px;object-fit:contain;" />`
      : "";
    const custSigHTML  = isRealSignature(savedCustSig)
      ? `<img src="${savedCustSig}"  alt="Customer signature" />`
      : `<div class="sig-pin">&#10003; Verified via PIN</div>`;
    const staffSigHTML = isRealSignature(savedStaffSig)
      ? `<img src="${savedStaffSig}" alt="Staff signature" />`
      : `<div class="sig-pin">&#10003; Verified via PIN</div>`;
    const issueDateFormatted = new Date(issueDate).toLocaleDateString("en-CA", {
      year: "numeric", month: "long", day: "numeric",
    });
    return `
      <div class="header">
        <div class="logo">${logoHTML}</div>
        <div class="company-right">
          ${companyName    ? `<h2>${companyName}</h2>`    : ""}
          ${companySlog    ? `<p>${companySlog}</p>`       : ""}
          ${companyAddress ? `<p>${companyAddress}</p>`   : ""}
          ${(companyPhone || companyEmail) ? `<p>${[companyPhone, companyEmail].filter(Boolean).join(" | ")}</p>` : ""}
        </div>
      </div>
      <hr class="divider" />
      <div class="title">Loan Closure Declaration</div>
      <div class="subtitle">Work Order: ${workOrderNo} | Invoice: ${invoiceNo} | Date: ${todayFormatted()}</div>
      <hr class="divider" />
      <div class="body-text">
        <p>I, <strong>${customerName}</strong>, hereby declare that the loan (Invoice No. <strong>${invoiceNo}</strong>) issued against pledged item(s) has been fully repaid.</p>
        <p>Principal Amount: <strong>${formatCurrency(principalAmount)}</strong></p>
        <p>Loan Issue Date: <strong>${issueDateFormatted}</strong></p>
        <p>All principal and interest amounts have been settled in full. The pledged item(s) are hereby authorized for return to the borrower.</p>
        <p>This declaration is signed and agreed to by both parties on ${todayFormatted()}.</p>
      </div>
      <div class="sig-row">
        <div class="sig-box">
          <div class="sig-label">Customer Signature</div>
          ${custSigHTML}
          <div class="sig-line">${customerName}</div>
        </div>
        <div class="sig-box">
          <div class="sig-label">${shopName} Signature</div>
          ${staffSigHTML}
          <div class="sig-line">${shopName}</div>
        </div>
      </div>
    `;
  };

  const handlePrint = () => {
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow popups to print."); return; }
    win.document.write(getPrintHTML(buildPrintableHTML()));
    win.document.close();
    setTimeout(() => { win.print(); }, 600);
  };

  const buildContainerForPDF = async (): Promise<HTMLDivElement> => {
    let resolvedLogoUrl = companyLogoUrl ?? "";
    if (resolvedLogoUrl && !resolvedLogoUrl.startsWith("data:")) {
      resolvedLogoUrl = await fetchImageAsBase64(resolvedLogoUrl);
    }
    let html = buildPrintableHTML();
    if (companyLogoUrl && resolvedLogoUrl) {
      html = html.replace(companyLogoUrl, resolvedLogoUrl);
    }
    const container = document.createElement("div");
    container.style.width   = "190mm";
    container.style.padding = "0";
    container.innerHTML     = html;
    const style = document.createElement("style");
style.innerHTML = `
  body { font-family: helvetica, sans-serif; color: #000; margin: 0; padding: 0; }
  .header { width: 100%; border-top: 20px solid #000; padding-top: 12px; margin-bottom: 16px; 
            display: flex; justify-content: space-between; align-items: center; box-sizing: border-box; }
  .logo { flex-shrink: 0; width: 120px; }
  .logo img { max-height: 60px; max-width: 110px; object-fit: contain; }
  .company-right { text-align: right; flex: 1; padding-left: 12px; word-break: break-word; }
  .company-right h2 { margin: 0; font-size: 13px; }
  .company-right p  { margin: 2px 0; font-size: 10px; color: #555; }
      .divider { border: none; border-top: 1px solid #ddd; margin: 12px 0; }
      .title { text-align: center; font-size: 20px; font-weight: bold; margin-bottom: 4px; }
      .subtitle { text-align: center; font-size: 10px; color: #888; margin-bottom: 12px; }
      .body-text { font-size: 12px; line-height: 1.8; }
      .body-text p { margin: 4px 0; }
      .sig-row { display: table; width: 100%; margin-top: 24px; }
      .sig-box { display: table-cell; width: 45%; padding-right: 5%; vertical-align: top; }
      .sig-label { font-size: 10px; color: #888; margin-bottom: 6px; }
      .sig-box img { width: 100%; height: 80px; object-fit: contain; border: 1px dashed #ccc; display: block; }
      .sig-pin { text-align: center; padding: 16px; border: 1px dashed #ccc; color: green; font-size: 11px; }
      .sig-line { border-top: 1px solid #333; margin-top: 8px; padding-top: 4px; font-size: 10px; }
    `;
    container.prepend(style);
    return container;
  };

  const handleDownloadPDF = async () => {
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const container = await buildContainerForPDF();
      document.body.appendChild(container);
      await html2pdf()
        .set({
          margin: 10,
          filename: `declaration_${invoiceNo}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .save();
      document.body.removeChild(container);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Failed to generate PDF.");
    }
  };

  // ── Opens compose dialog (replaces direct send) ─────────────────────────
  const handleOpenEmailDialog = () => {
    if (!customerEmail?.trim()) {
      alert("Customer email is not available. Please update the customer profile.");
      return;
    }
    setEmailDialogOpen(true);
  };

  // ── Called by EmailComposeDialog on Send click ──────────────────────────
  const handleSendEmail = async (to: string, subject: string, message: string) => {
    setLoading(true);
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const container = await buildContainerForPDF();
      document.body.appendChild(container);

      const pdfBlob: Blob = await html2pdf()
        .set({
          margin: 10,
          filename: `declaration_${invoiceNo}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .outputPdf("blob");

      document.body.removeChild(container);

      const pdfFile = new File(
        [pdfBlob],
        `declaration_${invoiceNo}.pdf`,
        { type: "application/pdf" }
      );
      const formData = new FormData();
      formData.append("to",           to.trim());
      formData.append("workOrderNo",  workOrderNo);
      formData.append("subject",      subject);
    formData.append("message", message); 
      formData.append("attachments[]", pdfFile);

      await axios.post(`${API_URL}/send-email`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });

      setEmailDialogOpen(false);
      alert("Email sent successfully.");
    } catch (err: any) {
      console.error("Email send error:", err);
      alert(err?.response?.data?.message || "Failed to send email.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={500}>
                {viewOnly ? "View Declaration" : "Loan closure declaration"}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {viewOnly && loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} flexDirection="column" gap={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading declaration…</Typography>
            </Box>

          ) : step === "form" ? (
            <>
              <Box sx={{ backgroundColor: "grey.50", border: "1px solid", borderColor: "divider",
                borderRadius: 2, p: 2, mb: 2.5, fontSize: 13, lineHeight: 1.8, color: "text.secondary" }}>
                I, <Box component="strong" sx={{ color: "text.primary" }}>{customerName}</Box>, hereby
                confirm that the loan (Invoice No.{" "}
                <Box component="strong" sx={{ color: "text.primary" }}>{invoiceNo}</Box>) against
                pledged item(s) has been fully repaid. The principal amount of{" "}
                <Box component="strong" sx={{ color: "text.primary" }}>{formatCurrency(principalAmount)}</Box>{" "}
                and all applicable interest have been settled in full. The pledged item(s) are authorized for return.
                This declaration is made on{" "}
                <Box component="strong" sx={{ color: "text.primary" }}>{todayFormatted()}</Box>.
              </Box>

              <Grid container spacing={1.5} mb={2.5}>
                {[
                  { label: "Work Order", value: workOrderNo },
                  { label: "Invoice",    value: invoiceNo },
                  { label: "Principal",  value: formatCurrency(principalAmount) },
                  { label: "Rate",       value: `${interestRate}% / month` },
                ].map((item) => (
                  <Grid item xs={6} key={item.label}>
                    <Box sx={{ backgroundColor: "action.hover", borderRadius: 1.5, p: 1.2 }}>
                      <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                      <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SignaturePanel
                    label="Customer signature" canvasRef={custCanvasRef} signed={custSigned}
                    onSigned={() => { setCustSigned(true); setCustSignedVia("canvas"); }}
                    onCleared={() => { setCustSigned(false); setCustSignedVia(null); }}
                    pinValue={custPin} onPinChange={setCustPin}
                    onPinConfirm={handleCustPinConfirm} pinError={custPinError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SignaturePanel
                    label="Staff signature" canvasRef={staffCanvasRef} signed={staffSigned}
                    onSigned={() => { setStaffSigned(true); setStaffSignedVia("canvas"); }}
                    onCleared={() => { setStaffSigned(false); setStaffSignedVia(null); }}
                    pinValue={staffPin} onPinChange={setStaffPin}
                    onPinConfirm={handleStaffPinConfirm} pinError={staffPinError}
                  />
                </Grid>
              </Grid>
            </>

          ) : (
            <Box py={2}>
              <Box textAlign="center">
                <CheckCircleIcon sx={{ fontSize: 52, color: "success.main", mb: 1.5 }} />
                <Typography variant="h6" fontWeight={500} mb={0.5}>
                  {viewOnly ? "Signed Declaration" : "Declaration complete"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2.5}>
                  {viewOnly ? "Declaration for work order " : "Saved against work order "}
                  <strong>{workOrderNo}</strong>.
                </Typography>

                <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPDF} disabled={loading}>
                    Download PDF
                  </Button>
                  <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={loading}>
                    Print
                  </Button>
                  {/* ← Now opens compose dialog instead of sending directly */}
                  <Button variant="outlined" startIcon={<EmailOutlinedIcon />} onClick={handleOpenEmailDialog} disabled={loading}>
                    Send by email
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        {step === "form" && !viewOnly && (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button variant="outlined" onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!bothSigned || loading} onClick={handleSubmit}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
              {loading ? "Saving..." : "Generate declaration"}
            </Button>
          </DialogActions>
        )}

        {step === "success" && (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: "center" }}>
            <Button variant="outlined" onClick={onClose}>Close</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ── Email compose dialog — rendered outside main Dialog to avoid z-index issues ── */}
      <EmailComposeDialog
        open={emailDialogOpen}
        onClose={() => { if (!loading) setEmailDialogOpen(false); }}
        defaultTo={defaultEmailTo}
        defaultSubject={resolvedSubject}   // ← resolved
  defaultMessage={resolvedMessage} 
   templateName={declarationTemplateName} 
        onSend={handleSendEmail}
        sending={loading}
        autoSelectKeyword="declaration"
         customerName={customerName}
  invoiceNo={invoiceNo}
      />
    </>
  );
};

export default DeclarationDialog;